import { z } from 'zod'
import { BugSchema } from './projectListSchemas.ts'

export const ReviewerKeySchema = z.string().regex(/^[a-z0-9][a-z0-9-]{0,99}$/)
export type ReviewerKey = z.infer<typeof ReviewerKeySchema>
export const AttributeValueSchema = z.union([z.string().max(2000), z.number().finite(), z.boolean(), z.null()])
export const ObservationSchema = z.object({
  code: z.string().regex(/^[a-z0-9][a-z0-9-]{0,99}$/),
  description: z.string().min(1).max(5000),
  attributes: z.record(z.string().min(1).max(100), AttributeValueSchema).refine(value => Object.keys(value).length <= 30, 'At most 30 attributes'),
})
export const ReviewObservationsSchema = z.object({ observations: z.array(ObservationSchema).max(100) }).strict()
export const ReviewScreenshotRequestSchema = z.object({
  timestamp_ms: z.number().int().safe().nonnegative(),
  caption: z.string().trim().min(1).max(1000),
}).strict()
export const ReviewScreenshotImageSchema = z.object({
  url: z.string().url().refine(url => new URL(url).protocol === 'https:', 'Screenshot URL must use HTTPS'),
  key: z.string().min(1), mime_type: z.enum(['image/png', 'image/jpeg', 'image/webp']),
  bytes: z.number().int().positive(), sha256: z.string().regex(/^[a-f0-9]{64}$/),
})
const ReviewScreenshotSchema = ReviewScreenshotRequestSchema.extend({ image: ReviewScreenshotImageSchema })
export const ReviewScreenshotSelectionSchema = ReviewScreenshotRequestSchema.extend({ image: ReviewScreenshotImageSchema.optional() })
export const ReviewOutputSchema = ReviewObservationsSchema.extend({ screenshot: ReviewScreenshotSchema.optional() })
export const ReviewerSettingsSchema = z.object({
  enabled: z.boolean().default(false),
  create_journeys: z.boolean().default(false),
  sample_percent: z.number().min(0).max(100).default(100),
  max_reviews_per_day: z.number().int().min(0).max(10000).nullable().default(null),
  max_journeys_per_day: z.number().int().min(0).max(1000).nullable().default(null),
  quiet_minutes: z.number().int().min(1).max(1440).default(15),
})
export type ReviewerSettings = z.infer<typeof ReviewerSettingsSchema>
export const ReviewerCatalogSchema = z.object({
  deleted_codes: z.array(ObservationSchema.shape.code).max(500).optional(),
  codes: z.array(z.object({
    code: ObservationSchema.shape.code,
    description: z.string().min(1).max(3000),
    attributes: z.record(z.string().max(100), z.object({
      type: z.enum(['string', 'number', 'boolean']),
      description: z.string().min(1).max(1000),
    })),
  })).max(500),
}).refine(value => new Set(value.codes.map(c => c.code)).size === value.codes.length, 'Codes must be unique')
export const reviewerGuidanceKey = (key: ReviewerKey) => `session-reviewer:${key}`
export const JourneyProposalSchema = z.object({
  instructions: z.string().min(1).max(10000),
  expected_behavior: z.string().min(1).max(5000),
  timestamp_ms: z.number().int().safe().nonnegative().optional(),
  observation_indexes: z.array(z.number().int().nonnegative()).min(1).max(100),
})
export const ObservationPeriodSchema = z.object({
  from: z.string().datetime(),
  to: z.string().datetime(),
}).refine(period => Date.parse(period.from) < Date.parse(period.to), 'Period end must follow its start')
export const ReviewFilterSchema = z.object({
  period: ObservationPeriodSchema.optional(),
  code: z.string().max(100).default(''),
  attributes: z.record(z.string().max(100), AttributeValueSchema).default({}),
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
})
export type ReviewFilter = z.infer<typeof ReviewFilterSchema>
export const NewReviewerSchema = z.object({
  name: z.string().trim().min(1).max(100),
  goal: z.string().trim().min(1).max(1000),
  prompt: z.string().trim().min(1).max(20000),
})
export const ReviewerDefinitionSchema = NewReviewerSchema.omit({ prompt: true }).extend({ key: ReviewerKeySchema, prompt: NewReviewerSchema.shape.prompt.nullable(), default_prompt: NewReviewerSchema.shape.prompt.nullable().default(null), prompt_customized: z.boolean().default(false) })
export const ReviewerCardSchema = ReviewerDefinitionSchema.extend({
  open_bugs: z.number().default(0),
  settings: ReviewerSettingsSchema, reviews_today: z.number(), journeys_today: z.number(),
  reviewed_sessions: z.number(), submitted_sessions: z.number().default(0), observations: z.number(),
})
export const ReviewerListResponseSchema = z.object({ reviewers: z.array(ReviewerCardSchema), can_edit: z.boolean() })
export const ReviewerDetailsSchema = z.object({
  reviewer: ReviewerCardSchema,
  observations: z.array(z.object({ code: ObservationSchema.shape.code, description: z.string(), observations: z.number().int().nonnegative(), sessions: z.number().int().nonnegative() })),
  can_edit: z.boolean(),
})
export const ReviewerObservationRequestSchema = z.object({
  action: z.literal('observation'), reviewer: ReviewerKeySchema,
  operation: z.enum(['add', 'delete']), name: ObservationSchema.shape.code,
})
export const ReviewRequestSchema = z.discriminatedUnion('action', [
  NewReviewerSchema.extend({ action: z.literal('create'), request_id: z.string().uuid() }),
  z.object({ action: z.literal('settings'), reviewer: ReviewerKeySchema, settings: ReviewerSettingsSchema }),
  z.object({ action: z.literal('review'), reviewer: ReviewerKeySchema, session_ids: z.array(z.string().min(1)).min(1).max(100), request_id: z.string().uuid() }),
  z.object({ action: z.literal('journey'), run_id: z.string().min(1), proposal: JourneyProposalSchema }),
  z.object({ action: z.literal('prompt'), reviewer: ReviewerKeySchema, prompt: NewReviewerSchema.shape.prompt.nullable() }),
  ReviewerObservationRequestSchema,
])
export const ReviewerBugLinkSchema = z.object({
  id: z.string(), title: z.string(), status: z.string(), is_duplicate: z.boolean(),
})
export const ReviewerContextSchema = z.object({
  id: z.string(), user_email: z.string().nullable(), session_url: z.string(),
  session_started_at: z.string(), output: ReviewOutputSchema.nullable(),
  journey_id: z.string().nullable(), journey_name: z.string().nullable(),
  proposal: JourneyProposalSchema.nullable(), bugs: z.array(ReviewerBugLinkSchema),
})
export type ReviewerContext = z.infer<typeof ReviewerContextSchema>
export const ReviewerResponseSchema = z.object({
  deleted_codes: z.array(ObservationSchema.shape.code).default([]),
  reviewers: z.array(ReviewerCardSchema),
  can_edit: z.boolean(),
  totals: z.object({ sessions: z.number(), observations: z.number(), reviewed_sessions: z.number(), submitted_sessions: z.number().default(0), total_sessions: z.number() }),
  series: z.array(z.object({ time: z.string().datetime({ offset: true }), code: z.string(), count: z.number().int().nonnegative() })).default([]),
  codes: z.array(z.object({ code: z.string(), observations: z.number(), sessions: z.number() })),
  attributes: z.array(z.object({ key: z.string(), value: AttributeValueSchema, observations: z.number(), sessions: z.number() })),
  numeric: z.array(z.object({ key: z.string(), count: z.number(), sum: z.number(), average: z.number(), min: z.number(), max: z.number() })),
  journeys: z.array(z.object({
    journey_id: z.string(), name: z.string(), source_reviewer: ReviewerKeySchema, run_id: z.string(), session_id: z.string(),
  })).default([]),
  runs: z.array(z.object({
    id: z.string(), session_id: z.string(), session_url: z.string(), user_email: z.string().nullable(),
    session_started_at: z.string().datetime({ offset: true }).optional(),
    task_id: z.string(), status: z.string(), latest_status: z.string(), latest_task_id: z.string(), created_at: z.string(), output: ReviewOutputSchema.nullable(),
    context: ReviewerContextSchema.optional(),
    journey_reviewer: ReviewerKeySchema.nullable().optional(),
    journey_id: z.string().nullable(), journey_task_id: z.string().nullable(), journey_status: z.string().nullable(),
  })),
  page: z.number(), has_more: z.boolean(),
})
export type ReviewerResponse = z.infer<typeof ReviewerResponseSchema>

export const ReviewerSessionQuerySchema = z.object({
  from: z.string().datetime({ offset: true }).optional(),
  to: z.string().datetime({ offset: true }).optional(),
  identity: z.enum(['all', 'anonymous', 'identified']).default('all'),
  search: z.string().trim().max(200).default(''),
  page: z.number().int().min(0).max(100000).default(0),
})
export const ReviewerSessionPageSchema = z.object({
  sessions: z.array(z.object({ session_id: z.string(), session_url: z.string(), user_email: z.string().nullable(), first_received_at: z.string(), last_received_at: z.string() })),
  page: z.number(), has_more: z.boolean(),
})

export const ReviewerBugsResponseSchema = z.object({ bugs: z.array(BugSchema), reviews: z.record(z.string(), z.array(ReviewerContextSchema)).default({}), total: z.number(), page: z.number(), has_more: z.boolean() })
